// authMsal.ts

import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  AccountInfo,
} from "@azure/msal-browser";

import { jwtDecode } from "jwt-decode";
import _ from "lodash";
import { sessionSlice, AUTH_MODE } from "../redux/slices/session";
import { getUserInformation } from "../helpers/AppHelpers";
import { setTokenAndAddLoginSession } from "../utlis/transitionUtils";
import { getEnv } from "../utils/envConfig";

let store: any = null;

export const setStore = (storeInstance: any) => {
  store = storeInstance;
};

/* =====================================================
   MSAL CONFIG
===================================================== */

const msalConfig = {
  auth: {
    clientId: getEnv('VITE_CLIENT_ID')!,
    authority: getEnv('VITE_AUTHORITY')!,
    redirectUri: window.location.origin + "/getAToken",
    postLogoutRedirectUri: window.location.origin,
    navigateToLoginRequestUrl: false, // CRITICAL
  },
  cache: {
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: false,
  },
};

/* 
  FIX 1: Request access for your custom Backend API.
  If the frontend and backend share the same App Registration client ID:
  We request 'api://<client-id>/access_as_user' or '<client-id>/.default'
*/
export const scopesRequest = {
  scopes: [
    `${getEnv('VITE_CLIENT_ID')}/.default`, // Custom Backend Scope exposed in Azure Portal
    "openid",
    "profile",
    "email"
  ],
};

export const msalInstance = new PublicClientApplication(msalConfig);

/**
 * Initialization promise – must resolve before any MSAL API call.
 * Awaits both initialize() and handleRedirectPromise() so the
 * redirect response (auth code in the URL) is processed before
 * the React tree renders.
 */
export const msalInitPromise: Promise<void> = msalInstance
  .initialize()
  .then(() => msalInstance.handleRedirectPromise())
  .then((response) => {
    if (response) {
      // If we just came back from a redirect, set the active account
      msalInstance.setActiveAccount(response.account);
    }
  })
  .catch((error) => {
    // Clear stuck interaction state so the next attempt isn't blocked
    sessionStorage.removeItem("msal.interaction.status");
    console.error("MSAL initialization/redirect handling failed:", error);
  });

/* =====================================================
   HELPERS
===================================================== */

export const getAllAccounts = (): AccountInfo[] => {
  return msalInstance.getAllAccounts();
};

export const getActiveAccount = (): AccountInfo | null => {
  const accounts = msalInstance.getAllAccounts();
  return accounts.length > 0 ? accounts[0] : null;
};

export const isAuthenticated = (): boolean => {
  return msalInstance.getAllAccounts().length > 0;
};

/* =====================================================
   LOGIN
===================================================== */

export const loginRedirect = async () => {
  // Ensure MSAL is initialised before any interaction
  await msalInitPromise;

  // Prevent double interaction
  if (isAuthenticated()) return;

  await msalInstance.loginRedirect(scopesRequest);
};

/* =====================================================
   LOGOUT
===================================================== */

export const logout = async () => {
  const account = getActiveAccount();

  if (store) {
    store.dispatch(sessionSlice.actions.logout());
  }

  await msalInstance.logoutRedirect({
    account: account || undefined,
  });
};

/* =====================================================
   SILENT TOKEN
===================================================== */

export const getTokenSilent = async (): Promise<string | null> => {
  // Ensure MSAL is initialised before any interaction
  await msalInitPromise;

  try {
    const account = getActiveAccount();
    if (!account) {
      await loginRedirect();
      return null;
    }

    const response = await msalInstance.acquireTokenSilent({
      account,
      scopes: scopesRequest.scopes,
      forceRefresh: true,
    });

    setTokenAndAddLoginSession(response);

    // FIX 2: Return the accessToken for backend validation instead of the idToken!
    return response.accessToken || response.idToken;
  } catch (error: any) {
    if (
      error instanceof InteractionRequiredAuthError ||
      error?.errorCode === "timed_out"
    ) {
      // Clear stuck interaction state before redirecting
      sessionStorage.removeItem("msal.interaction.status");
      await msalInstance.loginRedirect(scopesRequest);
      return null;
    }

    throw error;
  }
};

/* =====================================================
   FORCE REFRESH TOKEN
===================================================== */

export const refreshToken = async (): Promise<string | null> => {
  // Ensure MSAL is initialised before any interaction
  await msalInitPromise;

  try {
    const account = getActiveAccount();
    if (!account) {
      await loginRedirect();
      return null;
    }

    const response = await msalInstance.acquireTokenSilent({
      account,
      scopes: scopesRequest.scopes,
      forceRefresh: true,
    });

    setTokenAndAddLoginSession(response);

    // FIX 2: Return the accessToken for backend validation instead of the idToken!
    return response.accessToken || response.idToken;
  } catch (error: any) {
    if (
      error instanceof InteractionRequiredAuthError ||
      error?.errorCode === "timed_out"
    ) {
      // Clear stuck interaction state before redirecting
      sessionStorage.removeItem("msal.interaction.status");
      await msalInstance.loginRedirect(scopesRequest);
      return null;
    }

    throw error;
  }
};

/* =====================================================
   REDIRECT TOKEN (rare use)
===================================================== */

export const getTokenRedirect = async () => {
  await msalInstance.acquireTokenRedirect(scopesRequest);
};
